import React from "react";

export interface Address {
  building_or_house?: string;
  compound?: string;
  street?: string;
  neighborhood?: string;
  city?: string;
  center?: string;
  mobile?: string;
  latitude?: number | string;
  longitude?: number | string;
}

interface OrderAddressCardProps {
  address: Address;
  isRTL?: boolean;
  t: (key: string) => string;
}

const OrderAddressCard: React.FC<OrderAddressCardProps> = ({
  address,
  isRTL = false,
  t,
}) => {
  return (
    <div
      className={`mb-6 bg-gradient-to-r from-gray-50 to-white p-5 rounded-xl border border-gray-100 shadow-sm ${
        isRTL ? "text-right" : ""
      }`}
    >
      <div
        className={`flex items-center justify-between mb-3 ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <span className="text-blue-500">📍</span>
          {t("order.shippingAddress")}
        </h3>

        {address.latitude && address.longitude && (
          <div className="flex gap-2">
            <a
              href={`https://maps.google.com/?q=${address.latitude},${address.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
            >
              📍 {t("order.viewOnMap")}
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${address.latitude},${address.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
            >
              🚗 {t("order.getDirections")}
            </a>
          </div>
        )}
      </div>

      <div className="space-y-2 text-gray-600">
        {renderRow(t("order.building"), address.building_or_house, isRTL)}
        {renderRow(t("order.compound"), address.compound, isRTL)}
        {renderRow(t("order.street"), address.street, isRTL)}
        {renderRow(t("order.neighborhood"), address.neighborhood, isRTL)}
        {renderRow(t("order.city"), address.city, isRTL, true)}
        {renderRow(t("order.center"), address.center, isRTL)}
        {renderRow(t("order.mobile"), address.mobile, isRTL, true)}

        {address.latitude && address.longitude && (
          <div
            className={`flex items-center gap-3 mt-3 pt-3 border-t border-gray-200 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            <span
              className={`${isRTL ? "text-left" : "text-right"} w-24 text-gray-500 font-medium`}
            >
              {t("order.coordinates")}
            </span>
            <span className="font-mono text-sm">
              {address.latitude}, {address.longitude}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderAddressCard;

// Helper
const renderRow = (
  label: string,
  value?: string,
  isRTL?: boolean,
  bold?: boolean
) => {
  if (!value) return null;

  return (
    <div
      className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}
    >
      <span
        className={`${isRTL ? "text-left" : "text-right"} w-24 text-gray-500 font-medium`}
      >
        {label}
      </span>
      <span className={bold ? "font-medium" : ""}>{value}</span>
    </div>
  );
};